/**
 * Employee Tasks API Client
 *
 * API functions for employee task assignment operations.
 */

import { apiRequest } from './api';

/**
 * Task assignment
 */
export interface TaskAssignment {
  id: number;
  name: string;
  beschrijving: string | null;
  weging: number;
  /**
   * ISO-datumstring. De backend typeert dit als `Date`, maar levert het over
   * JSON als string; dit type beschrijft wat er daadwerkelijk binnenkomt.
   */
  assignedAt: string;
  /**
   * Of de onderliggende taak nog actief is. `false` = gearchiveerd: de rij
   * blijft in de lijst staan (archiveren verwijdert geen koppeling) maar telt
   * niet mee in `totalWeging`, in de tabbadge of in de statistieken.
   */
  isActief: boolean;
  warning?: string;
}

/**
 * Employee tasks response
 */
export interface EmployeeTasksResponse {
  employeeId: number;
  tasks: TaskAssignment[];
  totalWeging: number;
}

/**
 * Available task for assignment
 */
export interface AvailableTask {
  id: number;
  name: string;
  beschrijving: string | null;
  weging: number;
}

/**
 * Bulk assignment result
 */
export interface BulkAssignmentResult {
  assigned: number[];
  removed: number[];
  unchanged: number[];
}

/**
 * Employee Tasks API functions
 */
export const employeeTasksApi = {
  /**
   * Get tasks assigned to an employee
   */
  async getAssignedTasks(employeeId: number): Promise<EmployeeTasksResponse> {
    return apiRequest<EmployeeTasksResponse>(`/employees/${employeeId}/tasks`);
  },

  /**
   * Get available tasks for assignment
   */
  async getAvailableTasks(employeeId: number): Promise<AvailableTask[]> {
    return apiRequest<AvailableTask[]>(`/employees/${employeeId}/tasks/available`);
  },

  /**
   * Assign task to employee
   */
  async assignTask(employeeId: number, taskId: number): Promise<TaskAssignment> {
    return apiRequest<TaskAssignment>(`/employees/${employeeId}/tasks`, {
      method: 'POST',
      body: JSON.stringify({ taskId }),
    });
  },

  /**
   * Remove task assignment
   */
  async removeTask(employeeId: number, taskId: number): Promise<void> {
    return apiRequest<void>(`/employees/${employeeId}/tasks/${taskId}`, {
      method: 'DELETE',
    });
  },

  /**
   * Bulk replace task assignments
   */
  async bulkReplace(employeeId: number, taskIds: number[]): Promise<BulkAssignmentResult> {
    return apiRequest<BulkAssignmentResult>(`/employees/${employeeId}/tasks/bulk`, {
      method: 'PUT',
      body: JSON.stringify({ taskIds }),
    });
  },
};
